import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import sanityClient from "../../sanityClient";
import styled from "styled-components";
import { PortableText } from "@portabletext/react";
import { Helmet } from "react-helmet";

const PressReleaseContainer = styled.div`
  max-width: 800px;
  margin: 40px auto;
  padding: 20px;
  background-color: #f9f9f9;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
`;

const PressReleaseImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: 8px;
  margin-bottom: 20px;
`;

const PressReleaseTitle = styled.h1`
  font-size: 2.5em;
  margin-bottom: 20px;
  color: var(--hhb-primary);
  text-align: center;
`;

const PressReleaseMeta = styled.div`
  font-size: 1em;
  color: #666;
  margin-bottom: 20px;
  text-align: center;
`;

const PressReleaseBody = styled.div`
  font-size: 1.2em;
  line-height: 1.8;
  color: #333;
  margin-bottom: 20px;

  p {
    margin-bottom: 20px;
  }

  blockquote {
    font-style: italic;
    margin: 20px 0;
    padding: 10px 20px;
    background-color: #e9e9e9;
    border-left: 5px solid var(--hhb-primary);
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    margin-bottom: 20px;
  }
`;

const RecentReleasesContainer = styled.div`
  margin-top: 40px;
`;

const RecentReleasesTitle = styled.h2`
  font-size: 1.8em;
  margin-bottom: 20px;
  color: var(--hhb-primary);
  text-align: center;
`;

const RecentReleasesList = styled.ul`
  list-style: none;
  padding: 0;
`;

const RecentReleaseItem = styled.li`
  margin-bottom: 20px;
`;

const RecentReleaseLink = styled(Link)`
  text-decoration: none;
  color: var(--hhb-primary);
  font-size: 1.2em;

  &:hover {
    text-decoration: underline;
  }
`;

const SeeAllLink = styled(Link)`
  display: block;
  text-align: center;
  margin-top: 20px;
  font-size: 1.2em;
  color: var(--hhb-primary);

  &:hover {
    text-decoration: underline;
  }
`;

const PressRelease = () => {
  const { id } = useParams();
  const [release, setRelease] = useState(null);
  const [recentReleases, setRecentReleases] = useState([]);

  useEffect(() => {
    sanityClient
      .fetch(
        `*[_type == "pressRelease" && _id == $id]{
          title,
          body,
          "author": author->name,
          date,
          "imageUrl": mainImage.asset->url
        }`,
        { id }
      )
      .then((data) => setRelease(data[0]))
      .catch(console.error);
  }, [id]);

  useEffect(() => {
    sanityClient
      .fetch(
        `*[_type == "pressRelease" && _id != $id] | order(date desc)[0..2]{
          _id,
          title
        }`,
        { id }
      )
      .then((data) => setRecentReleases(data))
      .catch(console.error);
  }, [id]);

  if (!release) return <div>Loading...</div>;

  return (
    <>
      <Helmet>
        <title>{release.title} - Habitat for Humanity Buffalo</title>
        <meta
          name="description"
          content={release.body?.[0]?.children?.[0]?.text || release.title}
        />
      </Helmet>
      <PressReleaseContainer>
        {release.imageUrl && (
          <PressReleaseImage src={release.imageUrl} alt={release.title} />
        )}
        <PressReleaseTitle>{release.title}</PressReleaseTitle>
        <PressReleaseMeta>
          By {release.author} on {new Date(release.date).toLocaleDateString()}
        </PressReleaseMeta>
        <PressReleaseBody>
          <PortableText
            value={release.body}
            components={{
              block: {
                normal: ({ children }) => <p>{children}</p>,
                blockquote: ({ children }) => (
                  <blockquote>{children}</blockquote>
                ),
              },
              types: {
                image: ({ value }) => (
                  <img src={value.asset.url} alt={value.alt || "Image"} />
                ),
              },
            }}
          />
        </PressReleaseBody>
        <RecentReleasesContainer>
          <RecentReleasesTitle>Recent Press Releases</RecentReleasesTitle>
          <RecentReleasesList>
            {recentReleases.map((recentRelease) => (
              <RecentReleaseItem key={recentRelease._id}>
                <RecentReleaseLink to={`/newsroom/${recentRelease._id}`}>
                  {recentRelease.title}
                </RecentReleaseLink>
              </RecentReleaseItem>
            ))}
          </RecentReleasesList>
          <SeeAllLink to="/newsroom">See All Press Releases</SeeAllLink>
        </RecentReleasesContainer>
      </PressReleaseContainer>
    </>
  );
};

export default PressRelease;
